/**
 * ProtocolLinker — shared editable protocol assignment component.
 *
 * Used by LibrarySpecialtyAgent (specialty-level defaults) and
 * SubTenantPromptEdit (sub-tenant-level overrides).
 *
 * Props:
 *   specialty: string       — used to filter the available protocols
 *                              (only show protocols tagged with this specialty)
 *   currentLinks: array     — current linked_protocols: [{category, protocol_id}, ...]
 *   onSave: async (links)   — called with the new list on save
 *   busy: boolean
 */

import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";

import * as api from "../api.js";
import { Loading, ErrorBanner } from "../components.jsx";

function keyOf(p) {
  return `${p.category}:${p.protocol_id}`;
}

export default function ProtocolLinker({ specialty, currentLinks, onSave, busy }) {
  const [protocols, setProtocols] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selected, setSelected] = useState(
    new Set((currentLinks || []).map((p) => keyOf(p)))
  );
  const [dirty, setDirty] = useState(false);
  const [categoryFilter, setCategoryFilter] = useState("all");

  // Reset selection when the incoming currentLinks changes (e.g. after save + reload)
  useEffect(() => {
    setSelected(new Set((currentLinks || []).map((p) => keyOf(p))));
    setDirty(false);
  }, [currentLinks]);

  // Load all protocols filtered by specialty
  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const res = await api.listProtocols({ specialty });
        setProtocols(
          (res.data || []).sort((a, b) => {
            if (a.category !== b.category) return a.category.localeCompare(b.category);
            return a.protocol_id.localeCompare(b.protocol_id);
          })
        );
        setError(null);
      } catch (err) {
        setError(err);
      } finally {
        setLoading(false);
      }
    })();
  }, [specialty]);

  // Group protocols by category for display
  const grouped = useMemo(() => {
    const g = {};
    for (const p of protocols) {
      if (categoryFilter !== "all" && p.category !== categoryFilter) continue;
      if (!g[p.category]) g[p.category] = [];
      g[p.category].push(p);
    }
    return g;
  }, [protocols, categoryFilter]);

  const categories = useMemo(() => {
    const set = new Set(protocols.map((p) => p.category));
    return [...set].sort();
  }, [protocols]);

  const toggleOne = (p) => {
    const k = keyOf(p);
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(k)) next.delete(k);
      else next.add(k);
      return next;
    });
    setDirty(true);
  };

  const toggleCategory = (cat, checked) => {
    setSelected((prev) => {
      const next = new Set(prev);
      for (const p of protocols) {
        if (p.category !== cat) continue;
        const k = keyOf(p);
        if (checked) next.add(k);
        else next.delete(k);
      }
      return next;
    });
    setDirty(true);
  };

  const clearAll = () => {
    setSelected(new Set());
    setDirty(true);
  };

  const selectAll = () => {
    setSelected(new Set(protocols.map((p) => keyOf(p))));
    setDirty(true);
  };

  const save = async () => {
    const links = [...selected].map((k) => {
      const [category, protocol_id] = k.split(":");
      return { category, protocol_id };
    });
    await onSave(links);
    setDirty(false);
  };

  const resetToSaved = () => {
    setSelected(new Set((currentLinks || []).map((p) => keyOf(p))));
    setDirty(false);
  };

  if (loading) return <Loading label="Loading available protocols..." />;
  if (error) return <ErrorBanner error={error} />;

  if (protocols.length === 0) {
    return (
      <div className="empty-state">
        <h3>No protocols available</h3>
        <p>
          No protocols are tagged with specialty "{specialty}".{" "}
          <Link to="/library/draft/protocols/new">Create one</Link> or tag existing
          protocols with this specialty.
        </p>
      </div>
    );
  }

  return (
    <>
      <div className="mb-16" style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
        <span className="muted small">Filter:</span>
        <div className="btn-row" style={{ flexWrap: "wrap" }}>
          <button
            className={`btn sm ${categoryFilter === "all" ? "primary" : ""}`}
            onClick={() => setCategoryFilter("all")}
          >
            All ({protocols.length})
          </button>
          {categories.map((c) => (
            <button
              key={c}
              className={`btn sm ${categoryFilter === c ? "primary" : ""}`}
              onClick={() => setCategoryFilter(c)}
            >
              {c} ({protocols.filter((p) => p.category === c).length})
            </button>
          ))}
        </div>
        <div className="spacer" />
        <span className="muted small">
          {selected.size} of {protocols.length} linked
        </span>
        <div className="btn-row">
          <button className="btn sm" onClick={selectAll} disabled={busy}>
            Select all
          </button>
          <button className="btn sm" onClick={clearAll} disabled={busy}>
            Clear
          </button>
          <button className="btn sm" onClick={resetToSaved} disabled={busy || !dirty}>
            Reset
          </button>
          <button
            className="btn primary sm"
            onClick={save}
            disabled={busy || !dirty}
          >
            {busy ? "Saving..." : dirty ? "Save changes" : "Saved"}
          </button>
        </div>
      </div>

      {Object.entries(grouped).map(([cat, list]) => {
        const allSelectedInCat = list.every((p) => selected.has(keyOf(p)));
        const someSelectedInCat = list.some((p) => selected.has(keyOf(p)));
        return (
          <div key={cat} className="card" style={{ padding: 0 }}>
            <div
              style={{
                padding: "10px 16px",
                borderBottom: "1px solid var(--color-border)",
                background: "var(--color-surface-muted)",
                display: "flex",
                alignItems: "center",
                gap: 10,
              }}
            >
              <input
                type="checkbox"
                checked={allSelectedInCat}
                ref={(el) => {
                  if (el) el.indeterminate = !allSelectedInCat && someSelectedInCat;
                }}
                onChange={(e) => toggleCategory(cat, e.target.checked)}
                disabled={busy}
              />
              <strong style={{ fontSize: 13 }}>{cat}</strong>
              <span className="muted small">
                {list.filter((p) => selected.has(keyOf(p))).length} / {list.length}
              </span>
            </div>
            <ul
              style={{
                listStyle: "none",
                margin: 0,
                padding: 0,
              }}
            >
              {list.map((p) => {
                const k = keyOf(p);
                const on = selected.has(k);
                return (
                  <li
                    key={k}
                    style={{
                      padding: "10px 16px 10px 40px",
                      borderBottom: "1px solid var(--color-border)",
                      display: "flex",
                      alignItems: "center",
                      gap: 10,
                      background: on ? "var(--color-accent-subtle, transparent)" : "transparent",
                      cursor: busy ? "default" : "pointer",
                    }}
                    onClick={() => !busy && toggleOne(p)}
                  >
                    <input
                      type="checkbox"
                      checked={on}
                      onChange={() => toggleOne(p)}
                      disabled={busy}
                      onClick={(e) => e.stopPropagation()}
                    />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 13, fontWeight: 500 }}>
                        {p.name || p.protocol_id}
                      </div>
                      {p.description && (
                        <div className="muted small" style={{ marginTop: 2 }}>
                          {p.description}
                        </div>
                      )}
                    </div>
                    <span className="mono small muted">
                      {(p.content?.length || 0).toLocaleString()} chars
                    </span>
                  </li>
                );
              })}
            </ul>
          </div>
        );
      })}
    </>
  );
}
